import { shortAddressString, type AssetAddress, type LatLng } from '@aikoa/common'
import { useTranslations } from '@aikoa/i18n/react'
import { useQuery } from '@tanstack/react-query'
import { useCallback, useEffect, useRef, useState } from 'react'
import { ActivityIndicator, Pressable, View } from 'react-native'
import Animated, { FadeIn, FadeOut } from 'react-native-reanimated'
import { useSpinDelay } from 'spin-delay'
import { useDebounce } from 'use-debounce'
import Icon from '~/components/Icon'
import FloatingPopover from '~/components/ui/floating-popover'
import { Input, type InputVariant } from '~/components/ui/input'
import { Text } from '~/components/ui/text'
import { trpc, trpcClient } from '~/lib/trpc'
import { cn } from '~/lib/utils'

export type AddressResult = {
  address: AssetAddress
  location: LatLng
}

type ValidationState =
  | { status: 'empty' }
  | { status: 'typing' }
  | { status: 'validating' }
  | { status: 'valid'; result: AddressResult }
  | { status: 'invalid'; error: string }

export function AddressAutocomplete({
  value,
  onAddressChange,
  placeholder = 'Hae osoitetta...',
  className,
  inputClassName,
  disabled,
  inputVariant,
  countryCode = 'fi',
  biasLocation,
}: {
  value?: AddressResult | null
  onAddressChange: (result: AddressResult | null) => void
  placeholder?: string
  className?: string
  inputClassName?: string
  disabled?: boolean
  inputVariant?: InputVariant
  countryCode?: string
  biasLocation?: LatLng
}) {
  const { t } = useTranslations('common')
  const MIN_CHARS = 3

  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState(() =>
    value?.address ? shortAddressString(value.address) : ''
  )
  const [debouncedQuery] = useDebounce(query.trim(), 400)
  const blurTimer = useRef<NodeJS.Timeout | null>(null)

  const [isFromAutocomplete, setIsFromAutocomplete] = useState(!!value)
  const isFromAutocompleteRef = useRef(!!value)
  const [validationState, setValidationState] = useState<ValidationState>(
    value ? { status: 'valid', result: value } : { status: 'empty' }
  )
  const validationStateRef = useRef<ValidationState>(
    value ? { status: 'valid', result: value } : { status: 'empty' }
  )

  const lastQueryRef = useRef(query)

  useEffect(() => {
    if (value?.address && !query) {
      setQuery(shortAddressString(value.address))
      setIsFromAutocomplete(true)
      isFromAutocompleteRef.current = true
      const validState: ValidationState = { status: 'valid', result: value }
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-derived-state -- stfu nerd
      setValidationState(validState)
      validationStateRef.current = validState
    }
  }, [value, query])

  const canSearch = open && (debouncedQuery?.length ?? 0) >= MIN_CHARS

  const { data: results = [], isLoading } = useQuery(
    trpc.locations.googleAutocomplete.queryOptions(
      {
        input: debouncedQuery || '',
        countryCode,
        location: biasLocation,
      },
      {
        enabled: canSearch,
        staleTime: 1000 * 60 * 5,
      }
    )
  )

  const rawLen = query.trim().length
  const hasMinChars = rawLen >= MIN_CHARS
  const preMinCharsLoading = open && rawLen > 0 && !hasMinChars
  const fetchLoadingRaw = isLoading && hasMinChars

  const fetchLoading = useSpinDelay(fetchLoadingRaw, {
    delay: 150,
    minDuration: 350,
  })

  const showLoading = preMinCharsLoading || fetchLoading
  const hasResults = results.length > 0
  const rawEmpty = hasMinChars && !fetchLoadingRaw && !hasResults
  const showEmpty = useSpinDelay(rawEmpty, { delay: 200, minDuration: 400 })

  const handleSelect = useCallback(
    async (placeId: string, label: string) => {
      if (blurTimer.current) {
        clearTimeout(blurTimer.current)
        blurTimer.current = null
      }

      setOpen(false)
      setQuery(label)
      setIsFromAutocomplete(true)
      isFromAutocompleteRef.current = true
      setValidationState({ status: 'validating' })
      validationStateRef.current = { status: 'validating' }

      const details = await trpcClient.locations.googlePlaceDetails
        .query({ placeId })
        .catch(() => null)

      if (details) {
        const result: AddressResult = {
          address: details.address,
          location: details.location,
        }
        setValidationState({ status: 'valid', result })
        validationStateRef.current = { status: 'valid', result }
        setQuery(shortAddressString(details.address))
        onAddressChange(result)
      } else {
        const errorState: ValidationState = {
          status: 'invalid',
          // eslint-disable-next-line i18next/no-literal-string
          error: 'Osoitteen haku epäonnistui',
        }
        setValidationState(errorState)
        validationStateRef.current = errorState
        onAddressChange(null)
      }
    },
    [onAddressChange]
  )

  const handleTextChange = useCallback(
    (text: string) => {
      setQuery(text)
      lastQueryRef.current = text

      if (!open) {
        setOpen(true)
      }

      const updateValidationState = (newState: ValidationState) => {
        setValidationState(newState)
        validationStateRef.current = newState
      }

      if (isFromAutocomplete) {
        setIsFromAutocomplete(false)
        isFromAutocompleteRef.current = false
        updateValidationState(text.trim() ? { status: 'typing' } : { status: 'empty' })
        onAddressChange(null)
      } else if (validationState.status === 'valid') {
        updateValidationState(text.trim() ? { status: 'typing' } : { status: 'empty' })
        onAddressChange(null)
      } else if (!text.trim()) {
        updateValidationState({ status: 'empty' })
      } else {
        updateValidationState({ status: 'typing' })
      }
    },
    [open, isFromAutocomplete, validationState.status, onAddressChange]
  )

  const geocodeManualInput = useCallback(async () => {
    const currentQuery = lastQueryRef.current.trim()

    if (!currentQuery || isFromAutocompleteRef.current) {
      return
    }

    const currentValidationStatus = validationStateRef.current.status
    if (currentValidationStatus === 'valid' || currentValidationStatus === 'validating') {
      return
    }

    setValidationState({ status: 'validating' })
    validationStateRef.current = { status: 'validating' }

    const result = await trpcClient.locations.geocodeLocation
      .query({ location: currentQuery })
      .catch(() => null)

    if (lastQueryRef.current.trim() !== currentQuery) {
      return
    }

    if (isFromAutocompleteRef.current) {
      return
    }

    if (result && result.address.street) {
      const addressResult: AddressResult = {
        address: result.address,
        location: result.coordinates,
      }
      setValidationState({ status: 'valid', result: addressResult })
      validationStateRef.current = { status: 'valid', result: addressResult }
      onAddressChange(addressResult)
    } else {
      const errorState: ValidationState = {
        status: 'invalid',
        error: t(($) => $.location.addressNotFound),
      }
      setValidationState(errorState)
      validationStateRef.current = errorState
      onAddressChange(null)
    }
  }, [onAddressChange, t])

  const handleBlur = useCallback(() => {
    if (blurTimer.current) {
      clearTimeout(blurTimer.current)
    }

    blurTimer.current = setTimeout(() => {
      setOpen(false)

      const currentValidationStatus = validationStateRef.current.status

      if (
        !isFromAutocompleteRef.current &&
        lastQueryRef.current.trim().length >= MIN_CHARS &&
        currentValidationStatus !== 'valid' &&
        currentValidationStatus !== 'validating'
      ) {
        void geocodeManualInput()
      }
    }, 150)
  }, [geocodeManualInput])

  const isValid = validationState.status === 'valid'
  const isValidating = validationState.status === 'validating'
  const hasError = validationState.status === 'invalid'

  const resolvedAddress = validationState.status === 'valid' ? validationState.result : null

  return (
    <View className={cn('relative z-50 w-full gap-2', className)}>
      <FloatingPopover
        className="z-50"
        isOpen={open && !disabled && (showLoading || hasResults || showEmpty)}
        offset={6}
        contentClassName="rounded-lg bg-background max-h-[300px] gap-1"
        trigger={() => (
          <View className="relative">
            <Input
              placeholder={placeholder}
              value={query}
              onChangeText={handleTextChange}
              onFocus={() => setOpen(true)}
              onBlur={handleBlur}
              editable={!disabled}
              className={cn('w-full pr-10', hasError && 'border-destructive', inputClassName)}
              clearTextOnFocus={false}
              showClearButton={false}
              variant={inputVariant}
            />
            <View className="absolute right-3 top-1/2 -translate-y-1/2">
              {isValidating && <ActivityIndicator size="small" />}
              {isValid && !isValidating && (
                <Icon name="checkmark-circle" size={20} className="text-secondary" />
              )}
              {hasError && !isValidating && (
                <Icon name="alert-circle" size={20} className="text-error" />
              )}
            </View>
          </View>
        )}>
        <View className="flex-1 p-1.5">
          {showLoading && (
            <View className="flex-row items-center gap-2 p-3">
              <ActivityIndicator size="small" />
              <Text>Ladataan...</Text>
            </View>
          )}

          {!showLoading && hasResults && (
            <View className="w-full flex-1">
              {results.slice(0, 5).map((item) => (
                <Pressable
                  key={item.placeId}
                  className="group/item flex-row items-baseline justify-start gap-2 rounded-md bg-background px-4 py-3 transition-transform duration-150 hover:bg-accent active:scale-95"
                  onPress={() => handleSelect(item.placeId, item.label)}>
                  <Text className="text-left text-lg text-primary group-hover/item:text-accent-foreground">
                    {item.label}
                  </Text>
                  {!!item.discriminator && (
                    <Text className="text-sm text-secondary group-hover/item:text-accent-foreground">
                      {item.discriminator}
                    </Text>
                  )}
                </Pressable>
              ))}
            </View>
          )}

          {!showLoading && !hasResults && showEmpty && (
            <View className="p-3">
              {/* eslint-disable-next-line i18next/no-literal-string */}
              <Text className="text-secondary">Ei hakutuloksia.</Text>
            </View>
          )}
        </View>
      </FloatingPopover>

      {resolvedAddress && (
        <Animated.View
          entering={FadeIn.duration(150)}
          exiting={FadeOut.duration(100)}
          className="flex-row items-center gap-2 px-1">
          <Icon name="location" size={18} className="text-secondary" />
          <Text className="text-sm text-secondary">
            {shortAddressString(resolvedAddress.address)}
          </Text>
        </Animated.View>
      )}

      {hasError && (
        <Animated.View
          entering={FadeIn.duration(150)}
          exiting={FadeOut.duration(100)}
          className="px-1">
          <Text className="text-sm text-destructive">{validationState.error}</Text>
        </Animated.View>
      )}
    </View>
  )
}
